// src/components/auth/components/PasswordResetRequestForm.tsx
import React, { useState } from 'react';
import { COMPONENT_STYLES } from '@/theme';
import { useLanguage } from '@/hooks/useLanguage';
import { useResponsive } from '@/hooks/useResponsive';
import Message from '@/components/ui/Message';
import styles from './AuthModal.module.css';

interface PasswordResetRequestFormProps {
  onSwitchToLogin: () => void;
}

const PasswordResetRequestForm: React.FC<PasswordResetRequestFormProps> = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [isHuman, setIsHuman] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();
  useResponsive();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      if (!isHuman) {
        setError('auth.errors.recaptcha_required');
        setIsSubmitting(false);
        return;
      }
      
      const requestData = {
        email,
        is_human_check: isHuman
      };
      
      const response = await fetch('/api/password/reset-request/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      const data = await response.json() as { message?: string; error?: string };
      
      if (response.ok) {
        setMessage('auth.passwordReset.email_sent');
      } else {
        setError(data.error || 'auth.errors.general');
      }
    } catch (err) {
      setError('auth.errors.connection_error');
      console.error('Password reset request error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.authForm}>
      {error && <Message type="error" translateKey={error} />}
      {message && <Message type="success" translateKey={message} />}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className={styles.formGroup}>
          <label 
            htmlFor="email" 
            className={styles.formLabel}
          >
            {t('auth.common.email')}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail((e.target as any).value)}
            className={COMPONENT_STYLES.input.base}
            placeholder={t('auth.passwordReset.email_placeholder')}
            disabled={isSubmitting}
            required
          />
        </div>
        
        {/* Используем только простой чекбокс для подтверждения "я не робот" */}
        <div className={styles.rememberMe}>
          <input
            type="checkbox"
            id="isHuman"
            checked={isHuman}
            onChange={(e) => setIsHuman((e.target as any).checked)}
            className={styles.rememberMeCheckbox}
          />
          <label htmlFor="isHuman" className={styles.rememberMeLabel}>
            {t('auth.passwordReset.confirm_human')}
          </label>
        </div>
        
        <button
          type="submit"
          disabled={isSubmitting || !email || !isHuman}
          className={`w-full ${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.primary}`}
        >
          {isSubmitting 
            ? t('auth.passwordReset.button_sending')
            : t('auth.passwordReset.button_request')}
        </button>
      </form>
      
      <div className={styles.formFooter}>
        <p className="text-sm text-[#465357]">
          {t('auth.passwordReset.remember_password')}{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className={styles.switchLink}
          >
            {t('auth.passwordReset.back_to_login')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default PasswordResetRequestForm;